import { activeOrganizationId, graphql } from './client';

async function sha256(file: File): Promise<string> {
  const bytes = new Uint8Array(await crypto.subtle.digest('SHA-256', await file.arrayBuffer()));
  return [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
}

export async function uploadNoticeFile(input: { token: string; projectId: string; orderId?: string; file: File; kind: string }): Promise<string> {
  const checksum = await sha256(input.file);
  const authorization = await graphql<{ createDocumentUpload: { document: { id: string }; uploadUrl: string } }>({
    query: 'mutation Upload($projectId:ID!,$orderId:ID,$kind:String!,$fileName:String!,$contentType:String!,$sizeBytes:String!,$sha256:String!){createDocumentUpload(projectId:$projectId,orderId:$orderId,kind:$kind,fileName:$fileName,contentType:$contentType,sizeBytes:$sizeBytes,sha256:$sha256){document{id} uploadUrl}}',
    variables: { projectId: input.projectId, orderId: input.orderId, kind: input.kind, fileName: input.file.name, contentType: input.file.type, sizeBytes: String(input.file.size), sha256: checksum }, token: input.token, organizationId: activeOrganizationId()
  });
  const upload = await fetch(authorization.createDocumentUpload.uploadUrl, { method: 'PUT', headers: { 'content-type': input.file.type, 'x-amz-meta-sha256': checksum }, body: input.file });
  if (!upload.ok) throw new Error(`The file upload failed (${upload.status}).`);
  await graphql({ query: 'mutation Complete($id:ID!){completeDocumentUpload(id:$id){id status}}', variables: { id: authorization.createDocumentUpload.document.id }, token: input.token, organizationId: activeOrganizationId() });
  return authorization.createDocumentUpload.document.id;
}

export async function uploadResearchEvidence(input: { token: string; orderId: string; file: File }): Promise<string> {
  const checksum = await sha256(input.file);
  const authorization = await graphql<{ authorizeOperatorResearchEvidence: { evidenceId: string; uploadUrl: string } }>({
    query: 'mutation Evidence($input:JSON!){authorizeOperatorResearchEvidence(input:$input){evidenceId uploadUrl}}',
    variables: { input: { orderId: input.orderId, fileName: input.file.name, contentType: input.file.type, sizeBytes: input.file.size, sha256: checksum } },
    token: input.token, organizationId: activeOrganizationId()
  });
  const upload = await fetch(authorization.authorizeOperatorResearchEvidence.uploadUrl, { method: 'PUT',
    headers: { 'content-type': input.file.type, 'x-amz-meta-sha256': checksum }, body: input.file });
  if (!upload.ok) throw new Error(`The evidence upload failed (${upload.status}).`);
  const id = authorization.authorizeOperatorResearchEvidence.evidenceId;
  await graphql({ query: 'mutation CompleteEvidence($id:ID!){completeOperatorResearchEvidence(id:$id)}', variables: { id },
    token: input.token, organizationId: activeOrganizationId() });
  return id;
}

/** Uploads an attorney's signed review letter for a rule version and returns the letter id to record the review with. */
export async function uploadRuleCounselLetter(input: { token: string; ruleVersionId: string; file: File }): Promise<string> {
  const checksum = await sha256(input.file);
  const authorization = await graphql<{ authorizeRuleCounselLetter: { letterId: string; uploadUrl: string } }>({
    query: 'mutation Letter($input:JSON!){authorizeRuleCounselLetter(input:$input){letterId uploadUrl}}',
    variables: { input: { ruleVersionId: input.ruleVersionId, fileName: input.file.name, contentType: input.file.type, sizeBytes: input.file.size, sha256: checksum } },
    token: input.token, organizationId: activeOrganizationId()
  });
  const upload = await fetch(authorization.authorizeRuleCounselLetter.uploadUrl, { method: 'PUT',
    headers: { 'content-type': input.file.type, 'x-amz-meta-sha256': checksum }, body: input.file });
  if (!upload.ok) throw new Error(`The letter upload failed (${upload.status}).`);
  return authorization.authorizeRuleCounselLetter.letterId;
}

/** A contract kept with a job. Only PDF and text are accepted; a file the browser did not type is typed by its name. */
export function contractUpload(file: File): File {
  const type = /\.pdf$/i.test(file.name) ? 'application/pdf' : 'text/plain';
  return file.type === type ? file : new File([file], file.name, { type });
}
export const uploadContract = (input: { token: string; projectId: string; file: File }) =>
  uploadNoticeFile({ token: input.token, projectId: input.projectId, file: contractUpload(input.file), kind: 'CONTRACT' });
